import { useEffect, useRef, useState } from "react";
import { CalendarDays, ChevronDown, Info, X } from "lucide-react";
import type { TimelineProtectedEvent } from "@nexus/widgets";
import {
  Command,
  CommandInput,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
} from "@nexus/ui";

import type { RecentResource } from "@/contexts/AppContext";
import type { MultiChartEvent } from "@/hooks/live/useMultiChartEvents";
import type { TimezoneMode } from "@/lib/dateFormat";
import { useEventSearch } from "@/pages/live/components/useEventSearch";
import { cn } from "@/lib/utils";

export interface ProtectedEventTierProps {
  events: TimelineProtectedEvent[];
  selectedId: string | null;
  loading?: boolean;
  error?: boolean;
  onChange: (protectedEvent: TimelineProtectedEvent) => void;
  onRetry?: () => void;
}

export interface EventScopeSelectorProps {
  events: MultiChartEvent[];
  recents: RecentResource[];
  selectedEvent?: MultiChartEvent | null;
  selectedEventId: string | null;
  timezone: TimezoneMode;
  loading?: boolean;
  error?: boolean;
  /** A transient, non-blocking message (e.g. "showing the first available event instead"). */
  notice?: string | null;
  onEventChange: (event: MultiChartEvent) => void;
  onClear: () => void;
  onRetry?: () => void;
  className?: string;
  /** Present only for dashboards that nest a protected-event tier under the event (Event Monitor). */
  protectedEvent?: ProtectedEventTierProps;
  showClearButton?: boolean;
}

/**
 * The Event (+ optional Protected event) scope control: one pill, one
 * popover, in the same visual family as {@link TenantScopeSelector} and
 * {@link AnalyticsTimeRangeControl} (`h-10`, bordered, `shadow-sm`) so the
 * scope cluster reads as one system rather than three separately-designed
 * controls. A selection is marked with a subtle cyan tint on the border and
 * icon rather than a filled background — that stronger treatment stays
 * reserved for the tenant scope, which echoes the app's top rail; repeating
 * it here read as heavier than the control warranted.
 *
 * When a dashboard nests protected events under the event (Event Monitor),
 * they live inside this same popover rather than a second control next to
 * it: the trigger shows the deepest resolved value (the channel, once one is
 * active) and a compact chip row at the top of the popover lets the viewer
 * switch it without leaving the control. One extra step to change the
 * channel, in exchange for one scope, one control, one place to look.
 */
export function EventScopeSelector({
  events,
  recents,
  selectedEvent,
  selectedEventId,
  timezone,
  loading = false,
  error = false,
  notice,
  onEventChange,
  onClear,
  onRetry,
  className,
  protectedEvent,
  showClearButton = true,
}: EventScopeSelectorProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (wasOpenRef.current && !open) triggerRef.current?.focus();
    wasOpenRef.current = open;
  }, [open]);

  const { query, onQueryChange, resultsList, reset } = useEventSearch({
    events,
    recents,
    selectedEventId,
    timezone,
    onSelect: (event) => {
      onEventChange(event);
      setOpen(false);
    },
    onClear: selectedEventId
      ? () => {
          onClear();
          setOpen(false);
        }
      : undefined,
  });

  function handleOpenChange(nextOpen: boolean) {
    setOpen(nextOpen);
    if (!nextOpen) reset();
  }

  if (error) {
    return (
      <button
        type="button"
        onClick={onRetry}
        disabled={!onRetry}
        className={cn(
          "flex h-10 min-w-40 items-center gap-2 rounded-lg border border-border bg-surface px-3 text-sm font-medium text-muted shadow-sm shadow-black/3 disabled:cursor-not-allowed",
          className,
        )}
        title="Events could not be loaded. Click to retry."
      >
        <span>Events unavailable</span>
        {onRetry ? <span className="text-xs font-medium text-brand-cyan">Retry</span> : null}
      </button>
    );
  }

  const activeProtected = protectedEvent?.events.find(
    (event) => event.protected_event_id === protectedEvent.selectedId,
  );
  const anomalyCount = activeProtected?.unresolved_anomaly_count ?? 0;
  // A protected event's own name is sometimes just the channel ("Canal+
  // Foot"), which is ambiguous on its own once there's more than one event on
  // that channel — pairing it with the parent event keeps the trigger
  // identifying a single, specific thing rather than just a channel.
  const parentLabel = selectedEvent?.event_label;
  const label = activeProtected
    ? parentLabel
      ? `${parentLabel} · ${activeProtected.channel || "Unlabelled"}`
      : activeProtected.channel || "Unlabelled"
    : (parentLabel ?? "Select event");
  const isSet = Boolean(selectedEvent);
  const activeNotice = selectedEventId ? notice : null;

  return (
    <div className={cn("flex min-w-0 shrink-0 items-stretch", className)}>
      <Command label="Search events" shouldFilter={false} loop className="contents">
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <button
              ref={triggerRef}
              type="button"
              disabled={loading}
              aria-label={selectedEvent ? `Change event, ${label}` : "Select event"}
              title={activeNotice ?? undefined}
              className={cn(
                "group relative flex h-10 min-w-40 max-w-80 items-center gap-2 rounded-lg border px-3 text-sm font-medium shadow-sm shadow-black/3",
                "transition-[background-color,border-color,color] duration-240 ease-[cubic-bezier(0.25,1,0.5,1)] motion-reduce:transition-none",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:cursor-not-allowed disabled:opacity-60",
                // Sharing one min/max between both states (rather than a fixed
                // width when empty and a separately-hugging one once set) means
                // the pill only ever grows from the same floor as data resolves
                // — not a mode switch from a fixed box to a content-fitted one
                // — so it doesn't visibly displace tenant/time next to it.
                isSet
                  ? "border-brand-cyan/40 bg-surface text-primary hover:border-brand-cyan/60"
                  : "border-border bg-surface text-primary hover:border-border",
              )}
            >
              <CalendarDays
                className={cn("size-4 shrink-0", isSet ? "text-brand-cyan" : "opacity-70")}
                aria-hidden
              />
              <span className="min-w-0 flex-1 truncate text-left">{label}</span>
              {anomalyCount > 0 ? (
                <span className="flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-status-error-bg px-1 text-[10px] font-semibold tabular-nums text-status-error-text">
                  {anomalyCount}
                </span>
              ) : null}
              {activeNotice ? (
                <span className="absolute -right-1 -top-1 flex size-3 items-center justify-center rounded-full bg-status-warn-bg text-status-warn-text">
                  <Info className="size-2.5" aria-hidden />
                </span>
              ) : null}
              <ChevronDown
                className="size-3.5 shrink-0 text-muted transition-transform duration-150 group-data-[state=open]:rotate-180 motion-reduce:transition-none"
                aria-hidden
              />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            collisionPadding={8}
            className="w-[min(24rem,calc(100vw-2rem))] overflow-hidden p-0"
          >
            {protectedEvent && selectedEventId ? (
              <ProtectedEventTier {...protectedEvent} onPicked={() => setOpen(false)} />
            ) : null}
            <CommandInput
              value={query}
              onValueChange={onQueryChange}
              placeholder="Search events, collections or IDs…"
              aria-label="Search events"
            />
            {resultsList}
          </PopoverContent>
        </Popover>
        {selectedEventId && showClearButton ? (
          <button
            type="button"
            onClick={onClear}
            aria-label="Clear event selection"
            title="Back to event list"
            className="ml-1 flex size-10 shrink-0 items-center justify-center rounded-lg text-muted hover:bg-surface-elevated hover:text-primary focus-visible:ring-2 focus-visible:ring-focus"
          >
            <X className="size-4" aria-hidden />
          </button>
        ) : null}
      </Command>
    </div>
  );
}

/**
 * The nested tier, at the top of the same popover: which protected event
 * (channel) within the selected event. A short row of chips rather than a
 * second dropdown — a dashboard's protected events are always a short, known
 * list, so there is nothing to search here.
 */
function ProtectedEventTier({
  events,
  selectedId,
  loading = false,
  error = false,
  onChange,
  onRetry,
  onPicked,
}: ProtectedEventTierProps & { onPicked: () => void }) {
  if (loading) {
    return (
      <div
        className="flex gap-2 border-b border-border px-3 py-2.5"
        role="status"
        aria-label="Loading protected events"
      >
        <Skeleton className="h-6 w-20 rounded-full" />
        <Skeleton className="h-6 w-24 rounded-full" />
        <Skeleton className="h-6 w-16 rounded-full" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex items-center justify-between border-b border-border px-3 py-2.5 text-xs text-status-error-text">
        <span>Protected events unavailable</span>
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="font-medium text-brand-cyan hover:underline"
          >
            Retry
          </button>
        ) : null}
      </div>
    );
  }
  if (events.length === 0) return null;

  return (
    <div className="border-b border-border p-2.5">
      <p className="mb-1.5 px-0.5 text-xs font-medium text-muted">Protected event</p>
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Protected events">
        {events.map((event) => {
          const selected = event.protected_event_id === selectedId;
          return (
            <button
              key={event.protected_event_id}
              type="button"
              onClick={() => {
                onChange(event);
                onPicked();
              }}
              aria-pressed={selected}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-control border px-2 py-1 text-xs font-medium transition-colors",
                selected
                  ? "border-brand-cyan/50 bg-brand-cyan/10 text-brand-navy dark:text-primary"
                  : "border-border bg-surface text-secondary hover:bg-surface-elevated",
              )}
            >
              <span>{event.channel || "Unlabelled"}</span>
              {event.unresolved_anomaly_count > 0 ? (
                <span className="font-semibold tabular-nums text-status-error-text">
                  {event.unresolved_anomaly_count}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
